import React from 'react';
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

// 🐱 Cat character SVG
const Cat: React.FC<{ x: number; y: number; scale: number; flip?: boolean }> = ({
  x,
  y,
  scale,
  flip = false,
}) => (
  <g transform={`translate(${x}, ${y}) scale(${flip ? -scale : scale}, ${scale})`}>
    {/* Body */}
    <ellipse cx="0" cy="20" rx="28" ry="22" fill="#F4A460" />
    {/* Head */}
    <circle cx="0" cy="-15" r="24" fill="#F4A460" />
    {/* Ears */}
    <polygon points="-20,-35 -10,-10 -30,-10" fill="#F4A460" />
    <polygon points="20,-35 10,-10 30,-10" fill="#F4A460" />
    {/* Inner ears */}
    <polygon points="-20,-30 -13,-14 -25,-14" fill="#FF9999" />
    <polygon points="20,-30 13,-14 25,-14" fill="#FF9999" />
    {/* Eyes */}
    <ellipse cx="-9" cy="-18" rx="5" ry="6" fill="#1a1a1a" />
    <ellipse cx="9" cy="-18" rx="5" ry="6" fill="#1a1a1a" />
    {/* Eye shine */}
    <circle cx="-7" cy="-21" r="2" fill="white" />
    <circle cx="11" cy="-21" r="2" fill="white" />
    {/* Nose */}
    <polygon points="0,-8 -4,-4 4,-4" fill="#FF6B6B" />
    {/* Mouth */}
    <path d="M-6,-3 Q0,2 6,-3" stroke="#1a1a1a" strokeWidth="1.5" fill="none" />
    {/* Whiskers left */}
    <line x1="-35" y1="-10" x2="-12" y2="-8" stroke="#888" strokeWidth="1" />
    <line x1="-35" y1="-6" x2="-12" y2="-6" stroke="#888" strokeWidth="1" />
    {/* Whiskers right */}
    <line x1="35" y1="-10" x2="12" y2="-8" stroke="#888" strokeWidth="1" />
    <line x1="35" y1="-6" x2="12" y2="-6" stroke="#888" strokeWidth="1" />
    {/* Tail */}
    <path
      d="M20,30 Q60,10 50,40 Q40,60 30,50"
      stroke="#CD853F"
      strokeWidth="8"
      fill="none"
      strokeLinecap="round"
    />
    {/* Legs */}
    <ellipse cx="-18" cy="38" rx="10" ry="7" fill="#CD853F" />
    <ellipse cx="18" cy="38" rx="10" ry="7" fill="#CD853F" />
    {/* Stripe markings */}
    <path d="M-10,-32 Q0,-38 10,-32" stroke="#CD853F" strokeWidth="2" fill="none" />
    <path d="M-8,-24 Q0,-28 8,-24" stroke="#CD853F" strokeWidth="1.5" fill="none" />
  </g>
);

// 🐒 Beruk (monkey) character SVG
const Beruk: React.FC<{ x: number; y: number; scale: number; flip?: boolean; armAngle?: number }> = ({
  x,
  y,
  scale,
  flip = false,
  armAngle = 0,
}) => (
  <g transform={`translate(${x}, ${y}) scale(${flip ? -scale : scale}, ${scale})`}>
    {/* Body */}
    <ellipse cx="0" cy="20" rx="22" ry="26" fill="#8B6914" />
    {/* Belly */}
    <ellipse cx="0" cy="22" rx="14" ry="17" fill="#D2A679" />
    {/* Head */}
    <circle cx="0" cy="-18" r="26" fill="#8B6914" />
    {/* Face plate */}
    <ellipse cx="0" cy="-14" rx="16" ry="18" fill="#D2A679" />
    {/* Ears */}
    <circle cx="-27" cy="-20" r="10" fill="#8B6914" />
    <circle cx="27" cy="-20" r="10" fill="#8B6914" />
    <circle cx="-27" cy="-20" r="6" fill="#D2A679" />
    <circle cx="27" cy="-20" r="6" fill="#D2A679" />
    {/* Eyes */}
    <circle cx="-8" cy="-22" r="5" fill="#1a1a1a" />
    <circle cx="8" cy="-22" r="5" fill="#1a1a1a" />
    <circle cx="-6" cy="-24" r="2" fill="white" />
    <circle cx="10" cy="-24" r="2" fill="white" />
    {/* Eyebrows (mischievous) */}
    <path d="M-14,-28 Q-8,-32 -3,-28" stroke="#4a3000" strokeWidth="2.5" fill="none" />
    <path d="M14,-28 Q8,-32 3,-28" stroke="#4a3000" strokeWidth="2.5" fill="none" />
    {/* Nose */}
    <ellipse cx="0" cy="-10" rx="5" ry="3" fill="#5a3a00" />
    <circle cx="-2" cy="-10" r="1.5" fill="#1a1a1a" />
    <circle cx="2" cy="-10" r="1.5" fill="#1a1a1a" />
    {/* Mouth (big grin) */}
    <path d="M-10,-4 Q0,6 10,-4" stroke="#1a1a1a" strokeWidth="2" fill="#FF9999" />
    {/* Teeth */}
    <rect x="-5" y="-4" width="4" height="4" rx="1" fill="white" />
    <rect x="1" y="-4" width="4" height="4" rx="1" fill="white" />
    {/* Arms - raised (animated) */}
    <g transform={`rotate(${armAngle}, -22, 5)`}>
      <path d="M-22,5 Q-50,-20 -55,-10" stroke="#8B6914" strokeWidth="12" fill="none" strokeLinecap="round" />
    </g>
    <g transform={`rotate(${-armAngle}, 22, 5)`}>
      <path d="M22,5 Q50,-20 55,-10" stroke="#8B6914" strokeWidth="12" fill="none" strokeLinecap="round" />
    </g>
    {/* Legs */}
    <ellipse cx="-14" cy="44" rx="9" ry="7" fill="#6B5010" />
    <ellipse cx="14" cy="44" rx="9" ry="7" fill="#6B5010" />
    {/* Tail */}
    <path
      d="M-20,30 Q-60,20 -55,50 Q-50,70 -35,60 Q-20,55 -25,45"
      stroke="#6B5010"
      strokeWidth="8"
      fill="none"
      strokeLinecap="round"
    />
  </g>
);

// Banana emoji
const Banana: React.FC<{ x: number; y: number; rotation: number; opacity: number }> = ({
  x,
  y,
  rotation,
  opacity,
}) => (
  <g transform={`translate(${x}, ${y}) rotate(${rotation})`} opacity={opacity}>
    <text fontSize="40" textAnchor="middle" dominantBaseline="middle">🍌</text>
  </g>
);

// Stars/sparkles
const Sparkle: React.FC<{ x: number; y: number; size: number; opacity: number }> = ({
  x,
  y,
  size,
  opacity,
}) => (
  <g transform={`translate(${x}, ${y})`} opacity={opacity}>
    <polygon
      points={`0,${-size} ${size * 0.3},${-size * 0.3} ${size},0 ${size * 0.3},${size * 0.3} 0,${size} ${-size * 0.3},${size * 0.3} ${-size},0 ${-size * 0.3},${-size * 0.3}`}
      fill="#FFD700"
    />
  </g>
);

export const CatBeruk: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps, durationInFrames } = useVideoConfig();

  // === SCENE PHASES ===
  // 0-60: Title / Intro
  // 60-150: Monkey enters dancing
  // 150-240: Cat enters chasing
  // 240-320: Monkey steals fish, cat panics
  // 320-390: Chase scene
  // 390-450: They make up and dance together
  // 450-500: Outro

  // Background gradient colors that cycle
  const bgHue = interpolate(frame, [0, durationInFrames], [200, 280]);

  // === INTRO PHASE (0-60) ===
  const titleOpacity = interpolate(frame, [0, 20, 40, 55], [0, 1, 1, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const titleScale = interpolate(frame, [0, 20], [0.5, 1], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });

  // === MONKEY DANCING (60-150) ===
  const berukEnterX = interpolate(frame, [60, 100], [width + 100, width / 2 + 80], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const berukBounce = Math.sin((frame - 60) * 0.3) * 15;
  const berukArmSwing = Math.sin((frame - 60) * 0.4) * 30;

  // === CAT ENTERS (150-240) ===
  const catEnterX = interpolate(frame, [150, 200], [-100, width / 2 - 100], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const catBounce = Math.sin(frame * 0.25) * 10;

  // === CHASE SCENE (240-390) ===
  const chaseProgress = interpolate(frame, [240, 390], [0, 1], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });

  const berukChaseX = interpolate(frame, [240, 320, 390], [width / 2 + 80, width - 80, 80], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const catChaseX = interpolate(frame, [240, 320, 390], [width / 2 - 100, width - 220, -60], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });

  const chaseRunBounce = Math.abs(Math.sin(frame * 0.5)) * 20;

  // === REUNION DANCE (390-460) ===
  const danceProgress = interpolate(frame, [390, 410], [0, 1], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const catDanceX = interpolate(frame, [390, 410], [catChaseX, width / 2 - 90], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const berukDanceX = interpolate(frame, [390, 410], [berukChaseX, width / 2 + 70], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const danceBounce = Math.sin(frame * 0.4) * 18;
  const danceArmAngle = Math.sin(frame * 0.4) * 40;

  // === OUTRO (460-500) ===
  const outroOpacity = interpolate(frame, [460, 480, 490, 500], [0, 1, 1, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });

  // Bananas flying around
  const banana1Opacity = interpolate(frame, [60, 80, 380, 400], [0, 0.9, 0.9, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const banana1X = width * 0.7 + Math.sin(frame * 0.1) * 40;
  const banana1Y = height * 0.3 + Math.cos(frame * 0.08) * 30;

  const banana2Opacity = interpolate(frame, [80, 100, 380, 400], [0, 0.8, 0.8, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const banana2X = width * 0.15 + Math.sin(frame * 0.12 + 1) * 30;
  const banana2Y = height * 0.25 + Math.cos(frame * 0.09 + 2) * 25;

  // Fish (the cat's precious fish!)
  const fishOpacity = interpolate(frame, [200, 220, 380, 400], [0, 1, 1, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const fishX = frame > 240 ? berukChaseX : width / 2;
  const fishY = height * 0.6 - (frame > 240 ? chaseRunBounce : 0);

  // Sparkles during dance
  const sparkleOpacity = interpolate(frame, [390, 410], [0, 1], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });

  // Ground Y position
  const groundY = height * 0.78;

  // Determine what phase we are in for positioning
  const phase = frame < 60 ? 'intro'
    : frame < 150 ? 'beruk-enter'
    : frame < 240 ? 'cat-enter'
    : frame < 390 ? 'chase'
    : 'dance';

  const activeBerukX = phase === 'intro' ? width + 100
    : phase === 'beruk-enter' || phase === 'cat-enter' ? berukEnterX
    : phase === 'chase' ? berukChaseX
    : berukDanceX;

  const activeBerukY = phase === 'beruk-enter' || phase === 'cat-enter'
    ? groundY + berukBounce
    : phase === 'chase' ? groundY - chaseRunBounce
    : groundY + danceBounce;

  const activeCatX = phase === 'intro' || phase === 'beruk-enter' ? -100
    : phase === 'cat-enter' ? catEnterX
    : phase === 'chase' ? catChaseX
    : catDanceX;

  const activeCatY = phase === 'cat-enter'
    ? groundY + catBounce
    : phase === 'chase' ? groundY - chaseRunBounce
    : groundY + danceBounce;

  const berukFlipped = phase === 'chase' && frame > 320;
  const catFlipped = phase === 'chase' && frame > 320;

  const activeBerukArmAngle = phase === 'beruk-enter' || phase === 'cat-enter'
    ? berukArmSwing
    : phase === 'dance' ? danceArmAngle
    : 0;

  // Exclamation / speech bubble
  const catPanicOpacity = interpolate(frame, [220, 240, 260, 310], [0, 1, 1, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });

  return (
    <AbsoluteFill style={{ background: 'transparent' }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Sky gradient background */}
        <defs>
          <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#87CEEB" />
            <stop offset="100%" stopColor="#E0F0FF" />
          </linearGradient>
          <linearGradient id="ground" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7CCD7C" />
            <stop offset="100%" stopColor="#4a9a4a" />
          </linearGradient>
        </defs>

        {/* Sky */}
        <rect x="0" y="0" width={width} height={height} fill="url(#sky)" />

        {/* Clouds */}
        <g opacity={0.8}>
          <ellipse cx={150 + Math.sin(frame * 0.01) * 20} cy="80" rx="60" ry="30" fill="white" />
          <ellipse cx={180 + Math.sin(frame * 0.01) * 20} cy="70" rx="50" ry="28" fill="white" />
          <ellipse cx={120 + Math.sin(frame * 0.01) * 20} cy="85" rx="45" ry="22" fill="white" />
        </g>
        <g opacity={0.8}>
          <ellipse cx={width - 200 + Math.sin(frame * 0.008 + 1) * 15} cy="100" rx="70" ry="32" fill="white" />
          <ellipse cx={width - 160 + Math.sin(frame * 0.008 + 1) * 15} cy="90" rx="55" ry="28" fill="white" />
        </g>

        {/* Sun */}
        <circle cx={width - 80} cy="80" r="45" fill="#FFD700" opacity={0.9} />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
          <line
            key={angle}
            x1={width - 80 + 50 * Math.cos((angle * Math.PI) / 180)}
            y1={80 + 50 * Math.sin((angle * Math.PI) / 180)}
            x2={width - 80 + 70 * Math.cos((angle * Math.PI) / 180)}
            y2={80 + 70 * Math.sin((angle * Math.PI) / 180)}
            stroke="#FFD700"
            strokeWidth="4"
            strokeLinecap="round"
          />
        ))}

        {/* Trees */}
        <g>
          {/* Tree 1 */}
          <rect x="60" y={groundY - 80} width="14" height="80" fill="#8B4513" />
          <circle cx="67" cy={groundY - 100} r="45" fill="#228B22" />
          <circle cx="50" cy={groundY - 80} r="35" fill="#2E8B57" />
          <circle cx="84" cy={groundY - 85} r="38" fill="#228B22" />
        </g>
        <g>
          {/* Tree 2 */}
          <rect x={width - 90} y={groundY - 100} width="16" height="100" fill="#8B4513" />
          <circle cx={width - 82} cy={groundY - 120} r="55" fill="#228B22" />
          <circle cx={width - 100} cy={groundY - 100} r="40" fill="#2E8B57" />
          <circle cx={width - 64} cy={groundY - 105} r="42" fill="#228B22" />
        </g>

        {/* Ground */}
        <rect x="0" y={groundY} width={width} height={height - groundY} fill="url(#ground)" />
        {/* Ground detail */}
        <ellipse cx={width / 2} cy={groundY} rx={width / 2} ry="15" fill="#6BBF6B" />

        {/* Flowers on ground */}
        {[80, 160, 280, 400, 520, 640, 760, 880].map((fx, i) => (
          <g key={i} transform={`translate(${fx}, ${groundY - 5})`}>
            <line x1="0" y1="0" x2="0" y2="25" stroke="#2d8a2d" strokeWidth="2" />
            <circle cx="0" cy="-2" r="8" fill={i % 3 === 0 ? '#FF6B9D' : i % 3 === 1 ? '#FFD700' : '#FF8C00'} />
          </g>
        ))}

        {/* Flying bananas */}
        <Banana x={banana1X} y={banana1Y} rotation={frame * 3} opacity={banana1Opacity} />
        <Banana x={banana2X} y={banana2Y} rotation={-frame * 2 + 45} opacity={banana2Opacity} />

        {/* Fish (being chased / stolen) */}
        <g transform={`translate(${fishX}, ${fishY})`} opacity={fishOpacity}>
          <text fontSize="35" textAnchor="middle" dominantBaseline="middle">🐟</text>
        </g>

        {/* === CHARACTERS === */}

        {/* Beruk (Monkey) */}
        <Beruk
          x={activeBerukX}
          y={activeBerukY}
          scale={0.85}
          flip={berukFlipped}
          armAngle={activeBerukArmAngle}
        />

        {/* Cat */}
        <Cat
          x={activeCatX}
          y={activeCatY}
          scale={0.85}
          flip={catFlipped}
        />

        {/* Sparkles during dance reunion */}
        {[
          { x: width / 2 - 20, y: groundY - 180 },
          { x: width / 2 + 60, y: groundY - 160 },
          { x: width / 2 - 80, y: groundY - 140 },
          { x: width / 2 + 20, y: groundY - 200 },
        ].map((pos, i) => (
          <Sparkle
            key={i}
            x={pos.x + Math.sin(frame * 0.2 + i) * 10}
            y={pos.y + Math.cos(frame * 0.2 + i) * 8}
            size={12}
            opacity={sparkleOpacity * (0.6 + Math.sin(frame * 0.3 + i) * 0.4)}
          />
        ))}

        {/* Cat panic speech bubble */}
        <g opacity={catPanicOpacity}>
          <ellipse
            cx={activeCatX - 10}
            cy={activeCatY - 120}
            rx="55"
            ry="30"
            fill="white"
            stroke="#333"
            strokeWidth="2"
          />
          {/* Bubble tail */}
          <polygon
            points={`${activeCatX - 10},${activeCatY - 90} ${activeCatX + 15},${activeCatY - 85} ${activeCatX},${activeCatY - 75}`}
            fill="white"
            stroke="#333"
            strokeWidth="2"
          />
          <text
            x={activeCatX - 10}
            y={activeCatY - 112}
            textAnchor="middle"
            fontSize="22"
          >
            IKAN KU!
          </text>
          <text
            x={activeCatX - 10}
            y={activeCatY - 94}
            textAnchor="middle"
            fontSize="18"
          >
            😱
          </text>
        </g>

        {/* Beruk taunting speech bubble (during chase) */}
        {frame > 260 && frame < 380 && (
          <g opacity={interpolate(frame, [260, 280, 360, 380], [0, 0.9, 0.9, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' })}>
            <ellipse
              cx={activeBerukX + 10}
              cy={activeBerukY - 120}
              rx="60"
              ry="30"
              fill="#FFFFCC"
              stroke="#333"
              strokeWidth="2"
            />
            <polygon
              points={`${activeBerukX},${activeBerukY - 90} ${activeBerukX - 15},${activeBerukY - 85} ${activeBerukX - 5},${activeBerukY - 75}`}
              fill="#FFFFCC"
              stroke="#333"
              strokeWidth="2"
            />
            <text
              x={activeBerukX + 10}
              y={activeBerukY - 126}
              textAnchor="middle"
              fontSize="18"
            >
              HAHAHA! 🐒
            </text>
            <text
              x={activeBerukX + 10}
              y={activeBerukY - 104}
              textAnchor="middle"
              fontSize="16"
            >
              Tangkap la!
            </text>
          </g>
        )}

        {/* Dance together label */}
        {frame > 405 && frame < 460 && (
          <g opacity={interpolate(frame, [405, 420, 450, 460], [0, 1, 1, 0], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' })}>
            <ellipse cx={width / 2} cy={groundY - 220} rx="160" ry="40" fill="#FF9999" opacity={0.9} />
            <text
              x={width / 2}
              y={groundY - 230}
              textAnchor="middle"
              fontSize="24"
              fontWeight="bold"
              fill="white"
              fontFamily="Arial, sans-serif"
            >
              Kawan Baik! 🎉
            </text>
            <text
              x={width / 2}
              y={groundY - 205}
              textAnchor="middle"
              fontSize="20"
              fill="white"
              fontFamily="Arial, sans-serif"
            >
              Best friends! ✨
            </text>
          </g>
        )}

        {/* === TITLE OVERLAY (Intro) === */}
        <g opacity={titleOpacity} transform={`scale(${titleScale}) translate(${(width / titleScale - width) / 2}, ${(height / titleScale - height) / 2})`}>
          {/* Title background */}
          <rect x={width / 2 - 280} y={height / 2 - 100} width="560" height="180" rx="20" fill="#FF6B35" opacity={0.92} />
          {/* Shadow */}
          <rect x={width / 2 - 276} y={height / 2 - 96} width="560" height="180" rx="20" fill="#CC4400" opacity={0.4} />
          <rect x={width / 2 - 280} y={height / 2 - 100} width="560" height="180" rx="20" fill="#FF6B35" opacity={0.92} />

          <text
            x={width / 2}
            y={height / 2 - 35}
            textAnchor="middle"
            fontSize="54"
            fontWeight="bold"
            fill="white"
            fontFamily="Arial Rounded MT Bold, Arial, sans-serif"
          >
            🐱 vs 🐒
          </text>
          <text
            x={width / 2}
            y={height / 2 + 20}
            textAnchor="middle"
            fontSize="32"
            fontWeight="bold"
            fill="#FFD700"
            fontFamily="Arial Rounded MT Bold, Arial, sans-serif"
          >
            Kucing &amp; Beruk!
          </text>
          <text
            x={width / 2}
            y={height / 2 + 55}
            textAnchor="middle"
            fontSize="20"
            fill="white"
            fontFamily="Arial, sans-serif"
          >
            A Remotion Adventure 🎬
          </text>
        </g>

        {/* === OUTRO === */}
        <g opacity={outroOpacity}>
          <rect x={width / 2 - 220} y={height / 2 - 80} width="440" height="140" rx="20" fill="#4CAF50" opacity={0.93} />
          <text
            x={width / 2}
            y={height / 2 - 20}
            textAnchor="middle"
            fontSize="36"
            fontWeight="bold"
            fill="white"
            fontFamily="Arial Rounded MT Bold, Arial, sans-serif"
          >
            🎉 Tamat! 🎉
          </text>
          <text
            x={width / 2}
            y={height / 2 + 25}
            textAnchor="middle"
            fontSize="24"
            fill="#FFD700"
            fontFamily="Arial, sans-serif"
          >
            Made with ❤️ &amp; Remotion
          </text>
          <text
            x={width / 2}
            y={height / 2 + 50}
            textAnchor="middle"
            fontSize="18"
            fill="white"
            fontFamily="Arial, sans-serif"
          >
            by Amir Ariff
          </text>
        </g>

      </svg>
    </AbsoluteFill>
  );
};
